/* ==========================================================================
   Cart drawer v2
   Behaviour for sections/cart-drawer-v2.liquid.

   Nothing in assets/theme.js or assets/custom.js is modified. Instead the v2
   custom elements subclass the ones the theme already registered, which are
   retrieved with `customElements.get()`:

     <cart-drawer-v2>                  extends the theme's <cart-drawer>
     <free-shipping-bar-v2>            extends the theme's <free-shipping-bar>
     <cart-drawer-v2-recommendations>  collapses the cross-sell when it is empty

   The v2 markup keeps the theme's structural hooks (.line-item,
   .quantity-selector__*, data-line-id, input.freegiftId, .drawer__content,
   .drawer__footer) so the existing quantity / free-gift / shipping-insurance
   logic in custom.js keeps working untouched.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ATTRIBUTE = 'section';

  function whenDefined(name, callback) {
    if (window.customElements.get(name)) {
      callback(window.customElements.get(name));
      return;
    }

    window.customElements.whenDefined(name).then(function () {
      callback(window.customElements.get(name));
    });
  }

  /* ------------------------------------------------------------------------
     Stamped review badges

     snippets/product-rating.liquid renders an empty
     <span class="stamped-product-reviews-badge"> and Stamped's widget.min.js
     fills in the stars and the review count. It only scans for those spans when
     it initialises, and `_rerenderCart` below replaces the drawer's entire
     innerHTML on every cart change — so from the first add onwards the
     cross-sell cards carry badge spans Stamped has never seen, and the rating
     row renders as the empty 16px gap it reserves. The collection page shows the
     same card correctly because its markup is never swapped out.

     Two triggers, because the badges can be missing for either reason: the DOM
     swap above, and the drawer being opened (the widget is loaded `defer` at the
     end of layout/theme.liquid and initialised from its own onload handler, so a
     drawer opened early can beat it).
     ------------------------------------------------------------------------ */

  var STAMPED_RETRY_LIMIT = 20;
  var STAMPED_RETRY_DELAY = 250;
  var STAMPED_DEBOUNCE = 100;
  var stampedTimer = null;

  /**
   * Stamped's badge scanner under whichever name this build exposes. Null until
   * widget.min.js has loaded and myInit() has run.
   */
  function stampedBadgeLoader() {
    var stamped = window.StampedFn;
    if (!stamped) return null;
    if (typeof stamped.loadBadges === 'function') return stamped.loadBadges.bind(stamped);
    if (typeof stamped.reloadUGC === 'function') return stamped.reloadUGC.bind(stamped);
    return null;
  }

  function loadStampedBadges(attempt) {
    var load = stampedBadgeLoader();

    if (!load) {
      /* Widget not up yet. It is third party and deferred, so wait rather than
         give up on the first miss — but bound the wait so a store with Stamped
         removed does not poll forever. */
      if (attempt >= STAMPED_RETRY_LIMIT) return;
      setTimeout(function () {
        loadStampedBadges(attempt + 1);
      }, STAMPED_RETRY_DELAY);
      return;
    }

    try {
      load();
    } catch (error) {
      /* Stamped is third party; a failure in it must not take the drawer down. */
    }
  }

  function refreshStampedBadges() {
    /* `_rerenderCart` swaps the DOM and then opens the drawer, so both triggers
       fire within a frame of each other — collapse them into one scan so the
       badge API is not hit twice per add. */
    if (stampedTimer) clearTimeout(stampedTimer);

    stampedTimer = setTimeout(function () {
      stampedTimer = null;
      loadStampedBadges(0);
    }, STAMPED_DEBOUNCE);
  }

  /* ------------------------------------------------------------------------
     Free shipping bar continuity across a re-render

     `_rerenderCart` replaces the drawer's innerHTML wholesale, so the bar the
     `cart:updated` handler just animated is thrown away and the server rendered
     one paints straight at its final width — the CSS transition never gets a
     previous value to run from. These two carry the width that was actually on
     screen at the moment of the swap onto the new bar, then hand it its target
     so the transition picks up where the old one left off.
     ------------------------------------------------------------------------ */

  /** The fraction of the track the fill currently covers, or null if no bar is up. */
  function readShippingProgress(drawer) {
    var track = drawer.querySelector('.cd2-progress__track');
    var fill = drawer.querySelector('.cd2-progress__fill');
    if (!track || !fill) return null;

    var trackWidth = track.getBoundingClientRect().width;
    /* A closed drawer measures 0 across; there is no visible width to preserve. */
    if (!trackWidth) return null;

    return fill.getBoundingClientRect().width / trackWidth;
  }

  function restoreShippingProgress(drawer, previousProgress) {
    var bar = drawer.querySelector('free-shipping-bar-v2');
    if (!bar) return;

    /* No bar before this render means the cart was empty, so the fill starts
       from nothing and grows — same thing the shopper would have seen had the
       bar been there all along. */
    var start = previousProgress === null ? 0 : previousProgress;
    var target = bar.style.getPropertyValue('--cd2-progress');
    if (!target) return;

    bar.style.setProperty('--cd2-progress', String(start));
    /* Flush the start width so the browser has two values to interpolate
       between rather than collapsing both into one style recalculation. */
    void bar.offsetWidth;
    bar.style.setProperty('--cd2-progress', target);
  }

  /* `openable-element:open` bubbles up from the drawer (assets/theme.js). */
  document.addEventListener('openable-element:open', function (event) {
    if (event.target && event.target.tagName === 'CART-DRAWER-V2') {
      refreshStampedBadges();
    }
  });

  /* ------------------------------------------------------------------------
     <cart-drawer-v2>
     ------------------------------------------------------------------------ */

  whenDefined('cart-drawer', function (CartDrawer) {
    if (window.customElements.get('cart-drawer-v2')) return;

    var CartDrawerV2 = class extends CartDrawer {
      /**
       * The theme's implementation prefers `event.detail.cart.sections['mini-cart']`,
       * which renders the *default* drawer section. v2 always re-renders itself
       * from its own section id so the default section is never used here.
       */
      async _rerenderCart(event) {
        var sectionId = this.getAttribute(SECTION_ATTRIBUTE);
        var cartContent = (event && event.detail && event.detail.cart) || null;
        var url = window.themeVariables.routes.cartUrl + '?section_id=' + encodeURIComponent(sectionId);
        var response = await fetch(url);
        var html = await response.text();

        var fakeDiv = document.createElement('div');
        fakeDiv.innerHTML = html;

        var replacement = fakeDiv.querySelector('cart-drawer-v2');
        if (!replacement) return;

        var delay = (event && event.detail && event.detail.replacementDelay) || this.nextReplacementDelay || 0;

        setTimeout(() => {
          var scroller = this.querySelector('.drawer__content');
          var previousPosition = scroller ? scroller.scrollTop : 0;
          var previousProgress = readShippingProgress(this);

          this.innerHTML = replacement.innerHTML;

          restoreShippingProgress(this, previousProgress);

          /* The cross-sell cards that just landed carry badge spans Stamped has
             never scanned. Without this their stars and review count stay blank. */
          refreshStampedBadges();

          var newScroller = this.querySelector('.drawer__content');

          if (cartContent && cartContent.item_count === 0) {
            if (newScroller && typeof newScroller.animate === 'function') {
              newScroller.animate(
                { opacity: [0, 1], transform: ['translateY(40px)', 'translateY(0)'] },
                { duration: 450, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' }
              );
            }
          } else if (newScroller) {
            newScroller.scrollTop = previousPosition;
          }

          if (event && event.detail && event.detail.openMiniCart) {
            void this.clientWidth;
            this.open = true;
          }
        }, delay);

        this.nextReplacementDelay = 0;
      }
    };

    window.customElements.define('cart-drawer-v2', CartDrawerV2);
  });

  /* ------------------------------------------------------------------------
     <free-shipping-bar-v2>

     Only the variable name differs from the theme element, which writes
     `--progress`; the v2 bar reads `--cd2-progress` so theme.css cannot move it.

     This runs on `cart:updated`, which fires just before `cart:refresh` — the
     bar animates to its new width immediately and the section re-render that
     follows swaps in the matching threshold copy.
     ------------------------------------------------------------------------ */

  whenDefined('free-shipping-bar', function (FreeShippingBar) {
    if (window.customElements.get('free-shipping-bar-v2')) return;

    var FreeShippingBarV2 = class extends FreeShippingBar {
      _onCartUpdated(event) {
        if (!event.detail || !event.detail.cart || !Array.isArray(event.detail.cart.items)) return;

        var threshold = this.threshold;
        if (!threshold || !isFinite(threshold)) return;

        /* Shipping can be GIVEN rather than earned - a gift line, or the
           `_free shipping` marker 28-product-form writes on the paid line when a
           tier unlocks it. cart-drawer-v2.liquid already treats either as a full
           bar; without the same test here the fill dropped to the shippable
           subtotal while the header still read "Qualifies for Free Shipping",
           and the reward node slid back to the far left. Removing a paid line is
           where it showed, because what remains is $0.00 gifts. */
        var giftHandles = (this.getAttribute('gift-handles') || '')
          .split(',')
          .filter(Boolean);

        var gifted = event.detail.cart.items.some(function (item) {
          if (item.properties && item.properties['_free shipping']) return true;
          return giftHandles.indexOf(item.handle) !== -1;
        });

        if (gifted) {
          this.style.setProperty('--cd2-progress', '1');
          return;
        }

        var totalPrice = event.detail.cart.items
          .filter(function (item) {
            return item.requires_shipping;
          })
          .reduce(function (sum, item) {
            return sum + item.final_line_price;
          }, 0);

        var progress = Math.min(totalPrice / threshold, 1);
        this.style.setProperty('--cd2-progress', String(progress));
      }
    };

    window.customElements.define('free-shipping-bar-v2', FreeShippingBarV2);
  });

  /* ------------------------------------------------------------------------
     <cart-drawer-v2-recommendations>

     The grid is rendered server side by the section, which already excludes
     products present in the cart (Figma annotation: "Do not show products that
     are already in the cart"). This element only collapses the region when the
     exclusion leaves nothing to show — the drawer re-renders the whole section
     on every cart change, so no client-side fetching is needed.
     ------------------------------------------------------------------------ */

  if (!window.customElements.get('cart-drawer-v2-recommendations')) {
    var CartDrawerV2Recommendations = class extends HTMLElement {
      connectedCallback() {
        /*
          `.cd2-rec-card`, not `.pc28`: the redesign (Figma 14548:11447) replaced
          the shared `28-product-card` with a drawer-owned card, so `.pc28` no
          longer renders here and testing for it would collapse this region on
          every cart.
        */
        this.hidden = this.querySelector('.cd2-rec-card') === null;

        /*
          Show the first card in full and the top 15% of the next one, so the
          list reads as scrollable. Card heights depend on the copy, so they are
          measured rather than fixed, and re-measured when they change size.
        */
        this.grid = this.querySelector('.cd2-recs__grid');
        if (!this.grid || this.grid.querySelectorAll('.cd2-rec-card').length < 2) return;
        this.fit = this.fit.bind(this);
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.fit);
          this.observer.observe(this.grid);
        }
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fit);
        this.fit();
        this.onScroll = this.onScroll.bind(this);
        this.grid.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      fit() {
        var cards = this.grid.querySelectorAll('.cd2-rec-card');
        if (cards.length < 2) return;
        var first = cards[0].getBoundingClientRect().height;
        var second = cards[1].getBoundingClientRect().height;
        if (!first) return;
        var gap = parseFloat(getComputedStyle(this.grid).rowGap) || 0;
        this.grid.style.setProperty('--cd2-recs-max', Math.ceil(first + gap + second * 0.15) + 'px');
        this.grid.style.setProperty('--cd2-recs-fade', Math.ceil(gap + second * 0.15) + 'px');
        this.onScroll();
      }

      onScroll() {
        var end = this.grid.scrollTop + this.grid.clientHeight >= this.grid.scrollHeight - 2;
        this.grid.toggleAttribute('data-cd2-end', end);
      }
    };

    window.customElements.define('cart-drawer-v2-recommendations', CartDrawerV2Recommendations);
  }

  /* ------------------------------------------------------------------------
     Cross-sell card — quantity stepper + add to cart

     Previously this was left to the delegated
     `.purchase-details__buttons .product-form__add-button` handler in
     assets/custom.js. The redesign (Figma 14548:11447) gave the card its own
     quantity stepper, and that handler posts a hardcoded `quantity: 1` while
     ignoring the form, so it can no longer serve this card. The card carries
     v2-only classes instead, which also makes it impossible for both handlers
     to fire on one click and add the line twice.

     Delegated from `document` rather than bound per card: the drawer replaces
     its own innerHTML on every `cart:refresh`, so anything bound directly to a
     card would be discarded on the first add.
     ------------------------------------------------------------------------ */

  var REC_MIN_QTY = 1;
  var REC_MAX_QTY = 99;

  function recRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function recClamp(value) {
    if (isNaN(value) || value < REC_MIN_QTY) return REC_MIN_QTY;
    if (value > REC_MAX_QTY) return REC_MAX_QTY;
    return value;
  }

  function recReadQty(card) {
    var input = card.querySelector('.cd2-rec-card__qty');
    return recClamp(parseInt(input && input.value, 10));
  }

  function recWriteQty(card, value) {
    var input = card.querySelector('.cd2-rec-card__qty');
    if (input) {
      input.value = value;
    }

    var minus = card.querySelector('.cd2-rec-card__step--minus');
    if (minus) {
      minus.disabled = value <= REC_MIN_QTY;
    }
  }

  function recAddToCart(button, card) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) {
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    var item = { id: Number(variantId), quantity: recReadQty(card) };

    /*
      Add on the subscription plan when the card is advertising a subscription
      price. Without this the line is added at the full variant price while the
      card shows the discounted one — the card said $39.95 and the cart line
      said $59.95. The attribute is only present when the variant has an
      allocation, so one-off products still post a plain line.
    */
    var sellingPlanId = button.getAttribute('data-selling-plan-id');
    if (sellingPlanId) {
      item.selling_plan = Number(sellingPlanId);
    }

    fetch(recRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('add failed');
        }
        return response.json();
      })
      .then(function () {
        /*
          Same three steps the shared handler performs after a successful add, so
          the drawer, the insurance line and the open state stay consistent with
          every other add to cart path on the site.
        */
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );

        var drawer = document.getElementById('mini-cart');
        if (drawer && !drawer.open) {
          drawer.open = true;
        }

        if (typeof window.shippingInsuranceAddRemove === 'function') {
          window.shippingInsuranceAddRemove();
        }

        /*
          `cart:refresh` re-renders the drawer after a delay, which replaces this
          button outright. The spinner is held for that window rather than
          cleared immediately, so the label cannot flash back for a frame before
          the new markup lands — the same 500ms the shared handler in
          assets/custom.js waits. If the re-render somehow does not happen, this
          still restores the button rather than spinning forever.
        */
        setTimeout(function () {
          button.classList.remove('is-loading');
          button.disabled = false;
        }, 500);
      })
      .catch(function () {
        /*
          The drawer re-renders from the server on `cart:refresh`, so a failed add
          only needs the button returned to a usable state — there is no local
          state to roll back.
        */
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) {
      return;
    }

    var step = event.target.closest('[data-cd2-rec-step]');
    if (step) {
      var stepCard = step.closest('.cd2-rec-card');
      if (!stepCard) {
        return;
      }
      event.preventDefault();
      recWriteQty(
        stepCard,
        recClamp(recReadQty(stepCard) + parseInt(step.getAttribute('data-cd2-rec-step'), 10))
      );
      return;
    }

    var button = event.target.closest('[data-cd2-rec-add]');
    if (!button) {
      return;
    }

    var card = button.closest('.cd2-rec-card');
    if (!card) {
      return;
    }

    event.preventDefault();
    recAddToCart(button, card);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('.cd2-rec-card__qty')) {
      return;
    }

    var card = input.closest('.cd2-rec-card');
    if (card) {
      recWriteQty(card, recReadQty(card));
    }
  });
  /* ------------------------------------------------------------------------
     Spinners on the free-gift lines
     ------------------------------------------------------------------------

     A gift line has no controls of its own - it is not the customer's to
     change - so nothing ever put it in a loading state, and removing the paid
     line left its gifts sitting there looking settled while they were about
     to go. This mirrors the loader onto them.

     The loader contract is the theme's own, from the line-item-quantity
     element in assets/theme.js: reveal the wrapper and its first child (the
     spinner), keep the last child (the check mark) hidden.

     Which lines are the gifts comes from the paid line's own
     `input.freegiftId` values, the same list assets/custom.js reads to zero
     them, so the two can never disagree about what is about to happen.
     data-line-id is `<variantId>:<key>`, so the variant id is its head.
     ------------------------------------------------------------------------ */

  var GIFT_ID_INPUT = 'input.freegiftId';
  var LINE = '.cd2-line-item';

  function cd2Loader(line, on) {
    var loader = line.querySelector('.line-item__loader');
    if (!loader) return;
    loader.hidden = !on;
    if (loader.firstElementChild) loader.firstElementChild.hidden = !on;
    if (loader.lastElementChild) loader.lastElementChild.hidden = true;
  }

  /** The drawer lines granted by `line`, found by variant id. */
  function cd2GiftLinesOf(line) {
    var ids = [];
    line.querySelectorAll(GIFT_ID_INPUT).forEach(function (input) {
      var value = String(input.value || '').trim();
      if (value) ids.push(value);
    });
    if (!ids.length) return [];

    var out = [];
    var root = line.closest('cart-drawer-v2') || document;
    root.querySelectorAll(LINE).forEach(function (other) {
      if (other === line) return;
      var id = String(other.getAttribute('data-line-id') || '');
      var variant = id.split(':')[0];
      if (variant && ids.indexOf(variant) > -1) out.push(other);
    });
    return out;
  }

  function cd2SpinGifts(line) {
    cd2GiftLinesOf(line).forEach(function (giftLine) {
      cd2Loader(giftLine, true);
    });
  }

  /* Capture phase, so the gifts start spinning in the same tick the theme's own
     handler starts the paid line rather than a frame later. */
  document.addEventListener(
    'click',
    function (event) {
      /* Only controls that actually change the cart. `[data-line-id]` used to be
         in this list, and the line-item root carries that attribute too - so a
         click anywhere on the paid row, on its title or its image, set every
         gift spinning for a change that was never coming, and nothing cleared
         them because no cart event followed.

         The locked and disabled variants are out for the same reason: one
         renders as a span and the other as a button the stepper ignores, so
         clicking either mutates nothing. */
      var control = event.target.closest && event.target.closest(
        '.cd2-line-item__remove:not(.cd2-line-item__remove--locked),' +
          '.quantity-selector__button:not(.cd2-stepper__button--locked):not(.cd2-stepper__button--disabled)'
      );
      if (!control) return;
      var line = control.closest(LINE);
      if (line) cd2SpinGifts(line);
    },
    true
  );

  /* The drawer redraws on these, which clears the loaders with the markup. If a
     redraw does not come - a failed request - they would otherwise spin
     forever, so they are cleared here too. */
  ['cart:refresh', 'cart:updated'].forEach(function (name) {
    document.documentElement.addEventListener(name, function () {
      document.querySelectorAll('cart-drawer-v2 ' + LINE).forEach(function (line) {
        cd2Loader(line, false);
      });
    });
  });
})();
